// Editor.js block tool for a centered row of call-to-action buttons. Stores
// { buttons: [{ label, href, bg, fg }] }: `bg` is an optional custom
// background colour and `fg` its a11y-derived readable text colour (both ""
// when the button uses the theme accent). Legacy blocks stored a single
// button's fields at the top level — loaded here as a one-button row and
// migrated to the new shape on save. Rendered by render-blocks.ts (case
// "cta"). Link targets come from the reusable page-link field; colours from
// the tenant palette or a native picker. Legacy blocks may still carry a
// `text` supporting line — the renderer keeps showing it, but it's no longer
// editable and drops on re-save.

import { parseColor, rgbaToHex } from "@cms/ui/lib/color";
import { READABLE_TEXT_MIN_RATIO, readableTextOn } from "@cms/ui/lib/readable-text";
import { createPageLinkField } from "./page-link-field.js";

const ICON =
  '<svg width="17" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="8" width="18" height="8" rx="4"/></svg>';

function field(label, control) {
  const wrap = document.createElement("label");
  wrap.className = "pgs-cta-tool__field";
  const caption = document.createElement("span");
  caption.className = "pgs-cta-tool__label";
  caption.textContent = label;
  wrap.appendChild(caption);
  wrap.appendChild(control);
  return wrap;
}

function textInput(value, placeholder, onInput) {
  const input = document.createElement("input");
  input.type = "text";
  input.className = "pgs-cta-tool__input";
  input.value = value || "";
  input.placeholder = placeholder || "";
  input.addEventListener("input", () => onInput(input.value));
  return input;
}

/**
 * Background colour row: a "Theme" swatch (no custom colour), the tenant
 * palette, and a labelled "Custom" button opening the native picker — its
 * colour sample only shows while a custom colour is the selection. Selection
 * is by exact colour string, marked with a border; the readable text colour
 * is derived on save.
 */
export function colorRow(value, palette, onSelect) {
  const row = document.createElement("div");
  row.className = "pgs-color-row";
  let current = value || "";
  const isCustom = () => current !== "" && !palette.includes(current);

  const swatches = [];
  const refresh = () => {
    for (const { el, swatchValue } of swatches) {
      el.classList.toggle("pgs-color-row__swatch--active", swatchValue === current);
    }
    customBtn.classList.toggle("pgs-color-row__custom-btn--active", isCustom());
    sample.hidden = !isCustom();
    if (isCustom()) sample.style.backgroundColor = current;
  };
  const select = (next) => {
    current = next;
    onSelect(next);
    refresh();
  };

  const addSwatch = (swatchValue, title, background) => {
    const el = document.createElement("button");
    el.type = "button";
    el.className = "pgs-color-row__swatch";
    el.title = title;
    el.style.backgroundColor = background;
    el.addEventListener("click", () => select(swatchValue));
    swatches.push({ el, swatchValue });
    row.appendChild(el);
    return el;
  };

  // "" = theme accent (the site's default button colour).
  addSwatch("", "Theme colour", "var(--accent, #2563eb)");
  for (const color of palette) {
    addSwatch(color, color, color);
  }

  // "Custom" opens the (visually hidden) native picker.
  const customBtn = document.createElement("button");
  customBtn.type = "button";
  customBtn.className = "pgs-color-row__custom-btn";
  const sample = document.createElement("span");
  sample.className = "pgs-color-row__sample";
  customBtn.appendChild(sample);
  customBtn.appendChild(document.createTextNode("Custom"));

  const customInput = document.createElement("input");
  customInput.type = "color";
  customInput.className = "pgs-color-row__custom-input";
  customInput.setAttribute("aria-label", "Custom colour");
  const parsed = current ? parseColor(current) : null;
  customInput.value = parsed ? rgbaToHex(parsed) : "#2563eb";
  customInput.addEventListener("input", () => select(customInput.value));
  customBtn.addEventListener("click", () => customInput.click());

  row.appendChild(customBtn);
  row.appendChild(customInput);

  refresh();
  return row;
}

const MAX_BUTTONS = 4;

function cleanButton(raw) {
  return {
    label: typeof raw?.label === "string" ? raw.label : "",
    href: typeof raw?.href === "string" ? raw.href : "",
    bg: typeof raw?.bg === "string" ? raw.bg : "",
  };
}

export class CtaTool {
  static get toolbox() {
    return { title: "Buttons", icon: ICON };
  }

  constructor({ data, config, block }) {
    this.block = block;
    this.config = config ?? {};
    const raw = Array.isArray(data?.buttons) ? data.buttons : [data ?? {}];
    this.data = { buttons: raw.slice(0, MAX_BUTTONS).map(cleanButton) };
    if (this.data.buttons.length === 0) {
      this.data.buttons.push(cleanButton({}));
    }
  }

  render() {
    const root = document.createElement("div");
    root.className = "pgs-cta-tool";

    const title = document.createElement("div");
    title.className = "pgs-cta-tool__title";
    title.textContent = "Buttons (shown side by side)";
    root.appendChild(title);

    const list = document.createElement("div");
    list.className = "pgs-cta-tool__buttons";
    this._list = list;

    const add = document.createElement("button");
    add.type = "button";
    add.className = "pgs-gallery-tool__btn pgs-cta-tool__add";
    add.textContent = "+ Add button";
    add.addEventListener("click", () => {
      if (this.data.buttons.length >= MAX_BUTTONS) return;
      this.data.buttons.push(cleanButton({}));
      this._renderButtons();
      this.block?.dispatchChange?.();
      this._list.querySelector(".pgs-cta-tool__button:last-child input")?.focus();
    });
    this._add = add;

    root.append(list, add);
    this._renderButtons();

    return root;
  }

  _renderButtons() {
    const list = this._list;
    list.innerHTML = "";

    this.data.buttons.forEach((button, index) => {
      const row = document.createElement("div");
      row.className = "pgs-cta-tool__button";

      row.appendChild(field(
        "Button label",
        textInput(button.label, "Get a quote", (v) => {
          button.label = v;
          this.block?.dispatchChange?.();
        }),
      ));

      row.appendChild(field(
        "Button link",
        createPageLinkField({
          value: button.href,
          listOptions: this.config.listLinkOptions ?? (() => Promise.resolve([])),
          onChange: (href) => {
            button.href = href;
            this.block?.dispatchChange?.();
          },
        }),
      ));

      row.appendChild(field(
        "Button colour",
        colorRow(button.bg, this.config.getPalette?.() ?? [], (bg) => {
          button.bg = bg;
          this.block?.dispatchChange?.();
        }),
      ));

      if (this.data.buttons.length > 1) {
        const remove = document.createElement("button");
        remove.type = "button";
        remove.className = "pgs-gallery-tool__btn pgs-cta-tool__remove";
        remove.textContent = "Remove";
        remove.setAttribute("aria-label", `Remove button ${index + 1}`);
        remove.addEventListener("click", () => {
          this.data.buttons.splice(index, 1);
          this._renderButtons();
          this.block?.dispatchChange?.();
        });
        row.appendChild(remove);
      }

      list.appendChild(row);
    });

    this._add?.classList.toggle("pgs-cta-tool__add--hidden", this.data.buttons.length >= MAX_BUTTONS);
  }

  save() {
    return {
      buttons: this.data.buttons
        .map(({ label, href, bg }) => {
          const trimmedBg = bg.trim();
          return {
            label: label.trim(),
            href: href.trim(),
            bg: trimmedBg,
            fg: trimmedBg ? readableTextOn(trimmedBg, READABLE_TEXT_MIN_RATIO) : "",
          };
        })
        .filter((button) => button.label || button.href),
    };
  }
}
